/**
 * Shared Project Interface/Types
 */
import type { ImageProps } from "@bloomberg/media-ui-core/types";
import { type CmpClient } from "@foundation/cmp-client";
import { type ReactNode } from "react";
import { type TranslatedMessageProps } from "~utils/i18n/translate";

export * from "./i18n";
export * from "./portfolio";
export * from "./sectionfront";
export * from "./table";
export * from "./ticker-chart";

export interface HomePageProps {
  title: string;
}

export interface NextJsConfig {
  serverRuntimeConfig: Record<never, never>;
  publicRuntimeConfig: { devModeTest: string };
}

export interface DateTimeValue {
  dateTime: string;
  value: number;
}

export interface DateValue {
  date: string;
  value: number;
}

export type NumericDateTimeValue = {
  value: number;
  timestamp: number;
};

declare type IntradayRangeValue = {
  start: string;
  end: string;
};

export type IntradaySeriesValue = {
  series: DateTimeValue[];
  range?: IntradayRangeValue;
};

export type HistorySeriesValue = {
  series: DateValue[];
};

export interface Security {
  id: string;
  shortName: string;
  percentChange1Day: number;
  price: number;
  lastYield: number;
  yieldChange1Day: number;
  intradayPrices: IntradaySeriesValue;
}

export type SecurityListSecurity = {
  name: string;
  id: string;
  type: string;
  price: number;
  currency: string;
  percentChange: number;
  link?: string;
};

export type BarChartSecurity = {
  assetsQuarterly: DateTimeValue[];
  liabilitiesQuarterly: DateTimeValue[];
  debtQuarterly: DateTimeValue[];
  revenueQuarterly: DateTimeValue[];
  incomeQuarterly: DateTimeValue[];
  profitQuarterly: DateTimeValue[];
  operatingQuarterly: DateTimeValue[];
  investingQuarterly: DateTimeValue[];
  financingQuarterly: DateTimeValue[];
  assetsAnnual: DateTimeValue[];
  liabilitiesAnnual: DateTimeValue[];
  debtAnnual: DateTimeValue[];
  revenueAnnual: DateTimeValue[];
  incomeAnnual: DateTimeValue[];
  profitAnnual: DateTimeValue[];
  operatingAnnual: DateTimeValue[];
  investingAnnual: DateTimeValue[];
  financingAnnual: DateTimeValue[];
};

export type BarChartSecurityResponse = {
  securities: BarChartSecurity[];
};

export type FormattedBarChartSecurity = Record<
  keyof BarChartSecurity,
  NumericDateTimeValue[]
>;

export interface TickerBarSecurity {
  securities: Security[];
}

export type SecurityOverride = {
  name?: string;
  url?: string;
  useLastYield?: boolean;
};

export type SearchQueryTicker = {
  country: string;
  industry: string;
  is_hidden: boolean;
  name: string;
  resource_id: string;
  resource_subtype: string[];
  resource_type: string;
  score: number;
  security_type: string;
  ticker_symbol: string;
  url: string;
};

export type BoardMemberData = {
  id: string;
  name: string;
  companyName?: string;
  title?: string;
  currentPosition?: string;
  webVisibility: boolean;
};

export type TopHolding = {
  security: string;
  name: string;
  percentOfFund: string;
  value: string;
  position: string;
};

export type QuoteData = {
  averageDaysToMaturity: number | null;
  averageVolume30Day: number | null;
  backLoadFee: number | null;
  bicsSector: string | null;
  chartNews: {
    [key: string]: { point: DateTimeValue; articles: NewsArticle[] }[];
  };
  companyAddress: string | null;
  companyDescription: string | null;
  companyIsPrivate: boolean | null;
  companyPhone: string | null;
  companyWebsite: string | null;
  countryCode?: string;
  currentManagementFee: number | null;
  dayRange: string[] | null;
  dividend: number | null;
  earningsPerShare: number | null;
  exchangeDelay: string;
  exchangeId: string;
  expenseRatio: number | null;
  fiftyTwoWeekRange: string[] | null;
  foundedYear: string | null;
  frontLoadFee: number | null;
  fundAssetClassFocus: string | null;
  fundGeographicFocus: string | null;
  fundManager: string | null;
  fundMarketingFee: number | null;
  fundObjective: string | null;
  fundTopHoldings?: TopHolding[];
  fundType: string | null;
  hasChartNews: boolean | null;
  highPrice52Week: number | null;
  highPrice: number | null;
  id: string;
  inceptionDate: string | null;
  indexDescription: string | null;
  indexSource: string;
  indexMembers?: IndexMember[];
  indicatedGrossDividendYield: number | null;
  isAfterHours: boolean | null;
  isExchangeDelayed: boolean;
  isOpen: boolean | null;
  isRealTime: boolean;
  issuedCurrency: string;
  issuedUnits: string;
  lastDividendReported: number | null;
  lastPostSessionPrice: number;
  lastUpdate: string;
  locale: string;
  longName: string;
  lowPrice52Week: number | null;
  lowPrice: number | null;
  marketCap?: string | null;
  marketStatus: string;
  mediaSecuritySubtype: string;
  mediaSecurityType: string;
  name: string;
  netAssetValue: number | null;
  netAssetValueDate: string | null;
  netChange1DayPostSessionRegular: number;
  numberOfEmployees: number | null;
  openPrice: number | string | null;
  percentChange1Day: number;
  percentChange1DayPostRegular: number;
  percentPremium52WeekAverage: number | null;
  percentPremium: number | null;
  prevClose: string | undefined;
  previousClosingPriceOneTradingDayAgo: number | null;
  previousTradingDate: string;
  price: number;
  priceChange1Day: number;
  priceEarningsRatio: number | null;
  priceEarningsToGrowthAndDividendYieldRatio: number | null;
  priceMovements1Day: DateTimeValue[];
  priceMovements1Month: DateTimeValue[];
  priceMovements1Year: DateTimeValue[];
  priceMovements5Years: DateTimeValue[];
  priceMovements6Months: DateTimeValue[];
  priceMovementsYearToDate: DateTimeValue[];
  priceToBookRatio: number | null;
  priceToSalesRatio: number | null;
  primaryExchange: string;
  realTimeTickerDescription: string | null;
  redemptionFee: number | null;
  securityName: string;
  shareClass: string | null;
  sharesOutstanding: number | null;
  shortName: string;
  totalAssets: number | null;
  totalAssetsCurrency: number | null;
  totalAssetsDate: string | null;
  totalReturn1Year: string | number | null;
  totalReturn3Month: number | null;
  totalReturn3Year: number | null;
  totalReturn5Year: number | null;
  totalReturnYtd: string | number | null;
  tradingDate: string;
  tradingDayClose: string;
  type: string;
  ultimateParentTicker?: string;
  primarySecurityTicker?: string;
  issuerEquityTicker?: string;
  volume?: number | string | null;
};

export type BoardMembersAndExecutivesData = {
  boardMembers: BoardMemberData[];
  executives: ExecutiveData[];
};

export type SeriesKeys =
  | "priceMovements1Day"
  | "priceMovements1Month"
  | "priceMovements6Months"
  | "priceMovementsYearToDate"
  | "priceMovements1Year"
  | "priceMovements5Years"
  | "boardMembers"
  | "executives"
  | "fundTopHoldings"
  | "chartNews"
  | "indexMembers";

export type SearchQueryResult = {
  results: SearchQueryTicker[];
  total_results: number;
};

export type AdDemand = "extra-low" | "low" | "medium" | "high";

export type KeyStatRow = {
  key: string;
  value: string | number | null;
};

export interface ExecutiveData {
  id: string;
  name: string;
  shortName?: string;
  currentPosition?: string;
  personLink?: string;
  companyName?: string;
  webVisibility?: boolean;
  tenure?: string;
  thumbnail?: ImageProps;
}

type VideoType = {
  duration: number;
  story: {
    url: string;
    __typename: string;
  };
  __typename: string;
};

export type SecurityNews = {
  headline?: {
    seo?: string;
    text?: string;
    __typename: string;
  };
  id: string;
  type: string;
  primaryCategory?: string;
  thumbnail?: {
    url: string;
    __typename: string;
  };
  url?: string;
  duration?: string;
  videos: VideoType[];
  publishedAt?: string;
  updatedAt?: string;
};

export type PersonalizationSecurityNews = {
  headline: string;
  assetID: string;
  assetType: string;
  thumbnailUrl?: string;
  url?: string;
  duration?: string;
  score?: string;
  label?: string;
  publishedAt?: string;
};

export type SecurityNewsResults = {
  securityNews: SecurityNews[];
};

export type PaywallType = "reductive" | "additive";

export type RecentlyViewedSecurities = {
  longName: string;
  shortName: string;
  type: string;
  id: string;
  price: number;
  currency: string;
  percentChange: number;
  link?: string;
};

export type CompanyMetadata = BoardMembersAndExecutivesData & {
  companyAddress: string | null;
  companyDescription: string | null;
  companyIsPrivate: boolean;
  companyPhone: string | null;
  companyWebsite: string | null;
  foundedYear: string | null;
  numberOfEmployees: number | null;
};

export type PageMetadata = {
  bicsSector?: string;
  company?: CompanyMetadata;
  constructedTitle?: string | null;
  description: string;
  displayName: string;
  keywords?: string;
  pageType: string;
  title: string;
  type: string;
  url: string;
};

export type QuoteMetadata = PageMetadata & {
  mediaSecurityType: string;
  primaryExchange: string;
  securityName: string;
  shortExchange: string;
  shortTickerId: string;
  tickerId: string;
  tickerName: string;
};

export type RealTimeData = {
  [key: string]: string | number | boolean | null;
};

export type NewsArticle = {
  headline: string;
  url: string;
  publishedAt: Date;
};

export type MarketsWrapNewsArticle = {
  headline: string;
  url: string;
  updatedAt: Date;
  thumbnail: Image;
  body: {
    text: string;
    type: string;
  };
  mainMoves: string[];
};

export type NewsData = {
  point: DateTimeValue;
  articles: NewsArticle[];
};

export type PlugConfigKey =
  | "top-of-content"
  | "middle-of-content"
  | "bottom-of-content"
  | "left-rail";
export type PlugConfig = {
  [K in PlugConfigKey]?: string;
};

declare global {
  interface Window {
    newrelic: {
      noticeError: any;
      addPageAction: any;
    };
    plugClient: {
      init: (config: PlugConfig) => void;
    };
    NREUM?: {
      noticeError: (err: Error, attributes: Record<string, unknown>) => void;
      addPageAction(
        actionName: string,
        customAttributes?: Record<string, unknown>
      ): void;
    };
    __bloomberg__?: any;
    initializeAbbaScript: any;
    getCmpClient: () => CmpClient;
    cmpClient: {
      cmd: (() => void)[];
    };
    eventRecorder: {
      cmd: { push: (cb: any) => void };
    };
    getEventRecorderClient: any;
  }
}

export type WatchlistPerformanceChartData = Pick<
  QuoteData,
  | "id"
  | "name"
  | "priceMovements1Day"
  | "priceMovements1Month"
  | "priceMovements6Months"
  | "priceMovementsYearToDate"
  | "priceMovements1Year"
  | "priceMovements5Years"
> & {
  countryCode: string;
  exchangeCode: string;
};

export type QueryURL = {
  short: object;
  mid: object;
  long: object;
};

export type PriceMovementType = keyof Pick<
  QuoteData,
  | "priceMovements1Day"
  | "priceMovements1Month"
  | "priceMovements6Months"
  | "priceMovementsYearToDate"
  | "priceMovements1Year"
  | "priceMovements5Years"
>;

export type HeatmapTileTrend = "positive" | "negative" | "neutral";
export type HeatmapTileSeverity = "low" | "medium" | "high" | "neutral";

export type HeatmapTileData<T> = T & {
  severity: HeatmapTileSeverity;
  label: string;
  content?: ReactNode;
  trend: HeatmapTileTrend;
  size: number;
};

export type HeatmapTile<T> = {
  x: number;
  y: number;
  width: number;
  height: number;
  data: HeatmapTileData<T>;
};

export type TickerChartLegendData = {
  id: string;
  name: string;
  color: string;
  checked?: boolean;
  disabled?: boolean;
};

type ImageTheme = {
  id: string;
  url: string;
};

export type Image = {
  alt?: string;
  baseUrl?: string;
  caption?: string;
  credit?: string;
  height?: number;
  id?: string;
  origHeight?: number;
  origWidth?: number;
  size?: string;
  themes?: ImageTheme[];
  title?: string;
  type?: string;
  url?: string;
  width?: number;
  keywords?: string[];
};

export type NewsletterImage = Image & { placement?: string[] };

export type Newsletter = {
  slug: string;
  title: string;
  homepageDisplay?: {
    eyebrow: string;
    cardHeadline: string;
  };
  defaultEyebrow?: string;
  defaultHeadline?: string;
  images?: NewsletterImage[];
  primaryBrand?: string;
  description?: string;
  sampleUrl?: string;
  signUpList?: string;
  subscriptionType?: string;
};

export enum HEADLINE_SIZE {
  EXTRA_LARGE = "extraLarge",
  LARGE = "large",
  SMALL = "small",
  FULL_ZONE = "full",
  MEDIUM = "medium",
}

export type TodayInMarketsItem = {
  id: string;
  currency: string;
  percentChange: number;
  price: number;
  yesterdayPrice: number;
  volume: number;
  highPrice: number;
  lowPrice: number;
  // TODO: remove after TickerSparkline integration
  priceSet: number[];
  priceMovements?: DateTimeValue[];
  previousClosingPriceOneTradingDayAgo: number;
  priceChange1Day: number;
  volatility?: number;
};

export type TodayInMarketsCategory = {
  id: string;
  label: TranslatedMessageProps;
  dataItems: TodayInMarketsItem[];
};

export type IntradayChartData = IntradaySeriesValue & {
  id: string;
};

export type HistoryChartData = HistorySeriesValue & {
  id: string;
};
export type WatchlistBundleStyleVariant = "default" | "wrapped";

export type IndexMember = {
  id: string;
  lastUpdate: string;
  name: string;
  price: number;
  priceChange1Day: number;
  percentChange1Day: number;
  volume: number;
};

export type QuotePageData = {
  quotePageTab?: string;
};

export interface ArticleRecircStory {
  byline: string;
  headline: string;
  id: string;
  image?: Omit<Image, "url"> & { src: string };
  url: string;
}
